import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, EMPTY, BehaviorSubject } from 'rxjs';
import { take, finalize, catchError, tap, filter, switchMap, map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { WebhookService } from 'src/app/services/webhook.service';
import { WebhookFormValue } from 'src/app/models/webhook-configs';
import { URL_REGEX, noWhitespaceValidator } from 'src/app/common/utils/form-validators';
import { TranslateService } from '@ngx-translate/core';
import { NotificationToasterService } from 'src/app/services/notification-toaster.service';
import { SettingsNotificationService } from 'src/app/settings/shared/services/settings-notifications.service';
import {
  IntegrationsFormService,
  FormState,
  FormAction,
} from '../services/integrations-form.service';

/**
 * Component for managing webhook integration settings.
 * Handles enabling/disabling webhooks and configuring webhook URL and secret key.
 */
@UntilDestroy()
@Component({
  selector: 'app-user-settings-integrations-webhooks',
  templateUrl: './user-settings-integrations-webhooks.component.html',
  styleUrls: ['./user-settings-integrations-webhooks.component.scss'],
})
export class UserSettingsIntegrationsWebhooksComponent implements OnInit {
  protected readonly FormState = FormState;
  protected readonly formState$ = this.integrationsFormService.getFormState$();
  protected readonly form = this.createForm();
  protected readonly isInitialLoading$ = new BehaviorSubject<boolean>(true);

  constructor(
    private readonly fb: FormBuilder,
    private readonly webhookService: WebhookService,
    private readonly integrationsFormService: IntegrationsFormService,
    private readonly translateService: TranslateService,
    private readonly notificationToaster: NotificationToasterService,
    private readonly settingsNotificationService: SettingsNotificationService,
  ) {}

  ngOnInit(): void {
    this.loadSettings();
    this.handleFormSubmission();
  }

  private createForm(): FormGroup {
    const form = this.fb.group({
      enable: [false],
      url: ['', [Validators.required, Validators.pattern(URL_REGEX)]],
      secret: ['', [Validators.required, noWhitespaceValidator]],
    }) as FormGroup;

    form
      .get('enable')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe((enabled: boolean | null) => {
        const urlControl = form.get('url');
        const secretControl = form.get('secret');

        if (enabled) {
          urlControl?.enable();
          secretControl?.enable();
        } else {
          // Reset values and disable controls instead of removing them
          urlControl?.patchValue('');
          secretControl?.patchValue('');
          urlControl?.disable();
          secretControl?.disable();
          form.markAsUntouched();
        }
      });

    return form;
  }

  private loadSettings(): void {
    this.isInitialLoading$.next(true);
    let settings: { url?: string; secret?: string } | null = null;

    this.webhookService
      .getWebhookSettings()
      .pipe(
        take(1),
        tap((response) => (settings = response)),
        finalize(() => {
          this.handleSettingsLoad(settings);
          this.isInitialLoading$.next(false);
        }),
      )
      .subscribe({
        error: (error) => {
          this.notificationToaster.showDefaultErrorNotificationV2(
            this.translateService.instant('Failed to load webhook settings'),
            '',
            this.notificationToaster.generateNotificationCode(),
          );
        },
      });
  }

  private handleSettingsLoad(settings: { url?: string; secret?: string } | null): void {
    const formValues: WebhookFormValue = {
      enable: Boolean(settings?.url && settings?.secret),
      url: settings?.url ?? '',
      secret: settings?.secret ?? '',
    };

    this.form.patchValue(formValues);
    this.integrationsFormService.registerForm(this.form, formValues);
  }

  private handleFormSubmission(): void {
    this.integrationsFormService
      .getFormAction$()
      .pipe(
        untilDestroyed(this),
        filter((action) => action === FormAction.SUBMIT),
        switchMap(() =>
          this.submitForm().pipe(
            catchError(() => {
              this.settingsNotificationService.showFailureSettingsUpdateNotification();
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();
  }

  private submitForm(): Observable<void> {
    const request$ = this.form.get('enable')?.value
      ? this.webhookService
          .saveWebhookSettings({
            url: this.form.get('url')?.value ?? null,
            secret: this.form.get('secret')?.value ?? null,
          })
          .pipe(map(() => void 0))
      : this.webhookService.deleteWebhookSettings();

    return request$.pipe(
      tap(() => {
        this.integrationsFormService.updateInitialValue(this.form.getRawValue());
        this.form.markAsPristine();
        this.settingsNotificationService.showSuccessSettingsUpdateNotification();
      }),
      finalize(() => this.integrationsFormService.setLoading(false)),
    );
  }
}
